// /facility-dashboard wire schema.
//
// Org-wide operational tablero for facility_management admins. Returns
// the aggregates the dashboard needs in one round-trip plus a flat
// `entries` list the FE filters/sorts client-side. Recipe-driven keys
// (severity, category) — counts surface every recipe-declared value,
// including zeros.
//
// `byReportStatus` is the per-report status (reports.status), not a
// per-finding status — this schema has no finding-level lifecycle
// column. The dashboard maps "pendiente / en progreso / resuelto" onto
// the report status of each entry's parent acta, when it has one.
//
// An entry may be report-bound (born inside an acta's capture flow,
// facility's checklist materialization included — ADR-0066) or "loose"
// (created directly against the asset via POST /assets/:id/observations,
// ADR-0065). All five report-derived fields below (reportId,
// reportStatus, reportSignedAt, reportCreatedAt, reportType) are null on
// a loose entry — including reportSignedAt, whose null previously meant
// only "unsigned" and now also covers "no parent report at all".
import { OBSERVATION_PHASES } from "@bitacorapro/core-engine";
import { z } from "zod";
export const facilityDashboardEntry = z.object({
    id: z.string().uuid(),
    reportId: z.string().uuid().nullable(),
    reportStatus: z.string().nullable(),
    reportSignedAt: z.string().datetime().nullable(),
    reportCreatedAt: z.string().datetime().nullable(),
    reportType: z.string().nullable(),
    areaName: z.string(),
    element: z.string().nullable(),
    category: z.string(),
    severity: z.string(),
    humanDescription: z.string().nullable(),
    aiDescription: z.string().nullable(),
    // ADR-0117 — AI headline. `.default(null)` (not just `.nullable()`)
    // because this schema is an ingress parse on the dashboard (ADR-0021
    // D6): a core-api build that predates the field must still parse.
    aiTitle: z.string().nullable().default(null),
    assetId: z.string().uuid(),
    // Nullable per ADR-0020 Phase 6: the canonical client xlsx no longer
    // guarantees a Dirección column. The FE coalesces against assetUnit
    // when rendering the observation row.
    assetAddress: z.string().nullable(),
    assetUnit: z.string().nullable(),
    assetGroupId: z.string().uuid().nullable(),
    assetGroupName: z.string().nullable(),
    // Finding-level metadata so the FE can compute edadDias and the
    // observación lifecycle (status pill + SLA countdown) without a
    // second round-trip. delegationStatus is NULL until a provider is
    // assigned; the four timestamp/status fields move together through
    // the lifecycle described in findings.delegation_status comment.
    findingCreatedAt: z.string().datetime(),
    // ADR-0067 — when the observation was made, when that differs from
    // when the row was written. Non-null on checklist-materialized
    // findings (stamped from the parent acta's own timestamp). Null when
    // write time IS observation time: every live-capture path, and every
    // loose observation (ADR-0065), for which findingCreatedAt is the
    // correct anchor rather than a degraded fallback. Age/sort on both
    // server and client: findingObservedAt ?? findingCreatedAt.
    findingObservedAt: z.string().datetime().nullable(),
    // ADR-0134 — the three delegation fields are READ-ONLY history now:
    // facility runs the observation thread and nothing writes them any
    // more (the columns are dropped by a later contract migration, #0723).
    delegationStatus: z.string().nullable(),
    providerId: z.string().uuid().nullable(),
    delegatedAt: z.string().datetime().nullable(),
    resolvedAt: z.string().datetime().nullable(),
    // ADR-0134 — the observation thread, the lifecycle facility tracks
    // now. Same fields and same derivation as /postventa-dashboard/
    // observations: the status key, the PHASE the engine derives from the
    // graph (clients never compare status keys, fitness
    // observation-phase-single-source), the reopen count, the closure
    // overlay and the assignment window. Optional so a deployed dashboard
    // bundle keeps parsing (ADR-0021 D6).
    observationStatus: z.string().nullable().optional(),
    observationPhase: z.enum(OBSERVATION_PHASES).nullable().optional(),
    reopenCount: z.number().int().nonnegative().optional(),
    observationClosedAt: z.string().datetime().nullable().optional(),
    observationClosedReason: z.string().nullable().optional(),
    observationDueAt: z.string().datetime().nullable().optional(),
    // Evidence-thumbnail table cell (design pull, 2026-08-12) — replaces
    // the SLA countdown this entry used to carry (dropped: the founder
    // traded triage-by-deadline for documentation-richness on this
    // table). A batched count, never a per-row evidence fetch — one query
    // for the whole entries[] page, same pattern GET /assets/:id/observations
    // already uses for evidenceUrls.
    evidenceCount: z.number().int().nonnegative(),
    // Photo-thumbnail strip for the observation-table cell — the cell IS
    // the thumbnails, so they ride the aggregate instead of a lazy per-row
    // fetch. Deliberately unlike useVisitPhotos (2026-08-09 council,
    // queries.ts:172-197): that payload grew unboundedly with org age for
    // a gallery opened on 1-2 stores per session. This one has a fixed
    // ceiling (ENTRIES_LIMIT × ENTRY_THUMBS_LIMIT on the server) and is
    // rendered on every visible row.
    //
    // Photo-only, oldest-first (same insertion order as the detail
    // gallery), capped server-side — so length is NOT evidenceCount: that
    // count spans audio and notes too. Don't derive a "+N" overflow badge
    // from evidenceCount.
    //
    // No `.max()` on purpose: the cap is a server tuning knob; a wire-level
    // max would make raising it breaking for a cached bundle (ADR-0021/D6).
    //
    // `z.string()`, not `.url()`: this response is parsed on egress and
    // ingress, so one malformed legacy evidence.url row (text NOT NULL, no
    // CHECK) would 500 the whole tablero rather than degrade one cell.
    //
    // Raw stable URLs, same as GET /assets/:id/observations. Presigned
    // delivery (ADR-0107 fase 3) lands as a sibling field, per visible
    // page, expand-then-contract — never by re-typing this one.
    //
    // `.default([])` keeps a cached dashboard bundle parsing against a
    // core-api that predates this field, same as brandLogoUrl below.
    evidenceThumbUrls: z.array(z.string()).default([]),
});
export const facilityDashboardAssetGroup = z.object({
    id: z.string().uuid(),
    name: z.string(),
    // Brand-level metadata for the gerencial filter strip. brandId is
    // NULL on single-brand orgs and on arriendos/postventa verticals
    // that don't use the brand layer.
    brandId: z.string().uuid().nullable(),
    brandName: z.string().nullable(),
    // brands.config.logoUrl — the square logo committed through the admin
    // brand-logo flow. `.default(null)` keeps a cached dashboard bundle
    // parsing against a core-api that predates this field (ADR-0021/D6);
    // the server side stays required because z.infer's output type is
    // non-optional.
    brandLogoUrl: z.string().url().nullable().default(null),
    assetsCount: z.number().int().nonnegative(),
    findingsCount: z.number().int().nonnegative(),
});
export const facilityDashboardAsset = z.object({
    id: z.string().uuid(),
    // Nullable per ADR-0020 Phase 6: the canonical client xlsx no longer
    // guarantees a Dirección column. The FE displays unit as primary
    // identity and falls back to address only when present.
    address: z.string().nullable(),
    unit: z.string().nullable(),
    assetGroupId: z.string().uuid().nullable(),
    // Per-store brand. May be null even when the asset_group has a
    // brand_id (during the expand phase of the brand-normalization
    // migration). The FE coalesces: store.brandId ?? group.brandId.
    brandId: z.string().uuid().nullable(),
    brandName: z.string().nullable(),
    // Resolved from the same brand as brandName — see the assetGroup
    // schema above for why this carries a Zod default.
    brandLogoUrl: z.string().url().nullable().default(null),
    // Soft ref to auth_users.id. NULL when the store has no per-asset
    // supervisor — the dashboard falls back to the zona supervisor
    // (asset_groups.supervisorUserId) for display. Display-name
    // resolution against auth-svc happens client-side.
    supervisorUserId: z.string().uuid().nullable(),
    // Total findings (all delegation states) — kept for back-compat.
    findingsCount: z.number().int().nonnegative(),
    // Open findings — delegation_status NULL or NOT IN ('resuelto',
    // 'anulado'). This is what the dashboard "openObs" KPI counts.
    openFindingsCount: z.number().int().nonnegative(),
    // Most-recent signed acta_visita timestamp. NULL when the store has
    // never been visited (or never had a visit signed). Drives the
    // semáforo idle state when more than 45 days have passed.
    lastVisit: z.string().datetime().nullable(),
    // Latest scored visit score (1.00–5.00) and the 6 most recent
    // scores for the trend sparkline (most recent first). NULL/empty
    // when the store has no scored signed visits yet.
    score: z.number().nullable(),
    trend: z.array(z.number()),
    // Operational metrics. avgCloseDays averages (resolved_at -
    // delegated_at) over delegated+resolved findings on this asset.
    // recurrenceCount counts categories that recur across >1 report
    // for the asset (proxy for repeat issues).
    avgCloseDays: z.number().nullable(),
    recurrenceCount: z.number().int().nonnegative(),
});
// Per-asset visit history — feeds the "Historial de visitas" timeline
// on the store detail screen. Capped at 6 most-recent per asset in the
// route; the FE renders newest-first.
export const facilityDashboardVisit = z.object({
    reportId: z.string().uuid(),
    signedAt: z.string().datetime(),
    reportType: z.string(),
    score: z.number().nullable(),
    findingsCount: z.number().int().nonnegative(),
});
// Provider operational summary — feeds the "Contratistas" table on the
// Equipo screen. avgCloseDays uses only resolved findings; openCount
// is the live delegation queue length per provider.
export const facilityDashboardProvider = z.object({
    id: z.string().uuid(),
    shortName: z.string(),
    // Nullable per ADR-0020 Phase 6 — the canonical xlsx no longer
    // carries Razón social. UI displays shortName as the primary
    // identifier; legalName surfaces only when present.
    legalName: z.string().nullable(),
    // Nullable (Arrow pilot, 2026-08-10): the assign drawer's inline
    // "+ nuevo contratista" create path only requires name + email, so
    // providers created that way may carry no specialty until an
    // operator sets one in the dashboard. AssignDrawer's category-hint
    // heuristic must null-guard this.
    specialty: z.string().nullable(),
    status: z.string(),
    // Additive (Arrow pilot, 2026-08-10) — the assign drawer's confirm
    // step shows the resolved recipient before an irreversible email
    // fires. See the route's own comment on why this pragmatically lives
    // here rather than a dedicated providers directory for v1.
    contactEmail: z.string().nullable(),
    // Nullable per ADR-0020 Phase 6 — the canonical xlsx no longer
    // carries SLA default. Writer applies a 30-day default at ingest,
    // but historical rows may carry null until backfilled.
    defaultSlaDays: z.number().int().nonnegative().nullable(),
    openCount: z.number().int().nonnegative(),
    resolvedCount: z.number().int().nonnegative(),
    avgCloseDays: z.number().nullable(),
});
export const facilityDashboardResponse = z.object({
    totals: z.object({
        findings: z.number().int().nonnegative(),
        assets: z.number().int().nonnegative(),
        assetGroups: z.number().int().nonnegative(),
        reports: z.number().int().nonnegative(),
    }),
    // Counts keyed by recipe-declared severity / category slugs. Every key
    // declared in the active recipe appears in the map, even when zero.
    bySeverity: z.record(z.string(), z.number().int().nonnegative()),
    byCategory: z.record(z.string(), z.number().int().nonnegative()),
    // Counts keyed by reports.status (in_progress / analyzing / analyzed /
    // analysis_failed / signed). Same zero-fill convention.
    byReportStatus: z.record(z.string(), z.number().int().nonnegative()),
    byAssetGroup: z.array(facilityDashboardAssetGroup),
    byAsset: z.array(facilityDashboardAsset),
    byProvider: z.array(facilityDashboardProvider),
    // Map of assetId → up to 6 most-recent signed visits. Keyed for
    // O(1) lookup on the store detail screen.
    byAssetVisits: z.record(z.string().uuid(), z.array(facilityDashboardVisit)),
    entries: z.array(facilityDashboardEntry),
});
//# sourceMappingURL=facility-dashboard.js.map